import { Component } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subject, debounceTime, distinctUntilChanged } from "rxjs";
import * as FileSaver from "file-saver";
import { GenericFilterRequestDto } from "../../../core/models/generic-filter-request.dto";
import { ProductionWorkflowDeliveryStatusDto } from "../../../core/models/project-workflow/productionWorkflowDeliveryStatusDto";
import { WorkflowDeliveryStatusAdditionalFilters } from "../../../core/models/project-workflow/workflowDeliveryStatusAdditionalFilters";
import { ProjectWorkflowService } from "../../../core/services/project-workflow.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { CommonModule } from "@angular/common";
import { FilterMetadata } from "primeng/api";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { getShippingMethodDisplay } from "../../../core/utilities/carrier-shipping-method";

@UntilDestroy()
@Component({
  selector: "jni-delivery-status-list",
  templateUrl: "./delivery-status-list.component.html",
  styleUrls: ["./delivery-status-list.component.scss"],
  imports: [TableModule, CommonModule, SpinnerComponent],
})
export class DeliveryStatusListComponent {
  showSmallLoader = false;
  deliveryStatus = "Delivery exception";
  totalCount = 0;
  statusList: ProductionWorkflowDeliveryStatusDto[] = [];
  lazyLoadEvent!: TableLazyLoadEvent;
  defaultTake = 250;
  filterTextChanged: Subject<Event | null> = new Subject<Event | null>();
  filterDebounceTime = 1000;

  getShippingMethodDisplay = getShippingMethodDisplay;

  constructor(
    private _workflowService: ProjectWorkflowService,
    private _toastr: JniToastrService
  ) {}

  getWorkflowDeliveryStatuses(event: TableLazyLoadEvent) {
    this.showSmallLoader = true;
    this.lazyLoadEvent = event;
    const eventFilterArray = Object.keys(event.filters ?? {});
    const filter: GenericFilterRequestDto[] = eventFilterArray.map(x => {
      const tableFilter = event?.filters?.[x] as FilterMetadata;
      const data = {
        columnName: x ?? "",
        matchMode: tableFilter.matchMode ?? "",
        tableName: "",
        value: tableFilter.value ?? "",
      } as GenericFilterRequestDto;

      return data;
    });

    // const sortArray: GenericSortRequestDto[] =
    //   event.multiSortMeta?.map((x) => {
    //     return {
    //       ...x
    //     } as GenericSortRequestDto;
    //   }) || [];
    filter.forEach(x => {
      if (x.columnName == "workflowId") {
        x.columnName = "workflowId";
        x.tableName = "workflowHeader";
      }
      if (x.columnName == "salesOrderIdD365") {
        x.columnName = "SalesOrderIdD365";
        x.tableName = "salesOrderHeader";
      }
      if (x.columnName == "trackingNumber") {
        x.columnName = "trackingNumber";
        x.tableName = "tracking";
      }
      if (x.columnName == "deliveryStatus") {
        x.columnName = "deliveryStatus";
        x.tableName = "status";
      }
      if (x.columnName == "nickName") {
        x.columnName = "nickName";
        x.tableName = "salesOrderHeader";
      }
    });

    const request: WorkflowDeliveryStatusAdditionalFilters = {
      skip: event.first ?? 0,
      take: event.rows ?? this.defaultTake,
      deliveryStatus: this.deliveryStatus,
      filters: filter,
    };

    this._workflowService
      .getWorkflowDeliveryStatusList(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          this.totalCount = res.totalRecords ?? 0;
          this.statusList = res.data;
        },
        error: res => {
          this.showSmallLoader = false;
          this._toastr.error("Error", res.message || "Data not found");
        },
      });
  }

  getPaddedWorkflowId(workflowId: number) {
    return workflowId.toString().padStart(6, "0");
  }

  onFilterInput($event: Event | null) {
    if (this.filterTextChanged.observers.length === 0) {
      this.filterTextChanged
        .pipe(debounceTime(this.filterDebounceTime), distinctUntilChanged())
        .subscribe(() => {
          this.getWorkflowDeliveryStatuses(this.lazyLoadEvent);
        });
    }
    this.filterTextChanged.next($event);
  }

  getDeliveryStatusOnChange(value: MouseEvent) {
    const target = value.target as HTMLInputElement;
    this.deliveryStatus = target?.id;
    this.onFilterInput(null);
  }

  exportExcelForFilterData() {
    const object = this.statusList.map(datum => {
      return {
        ["Proposed Project"]: this.getPaddedWorkflowId(datum.workflowId),
        ["Sales Order Id"]: datum.salesOrderIdD365,
        ["Tracking Number"]: datum.trackingNumber,
        ["Line Number"]: datum.lineNumber,
        ["Nickname"]: datum.nickName,
        ["Shipping Method"]: datum.shippingMethodName,
        ...(this.deliveryStatus === "Delivered" && {
          ["Delivery Status"]: datum.deliveryStatus,
        }),
        ...(this.deliveryStatus === "Delivery exception" && {
          ["Delivery Exception"]: datum.deliveryStatus,
        }),
        ...(this.deliveryStatus === "Delivery exception" && {
          ["Description"]: datum.exceptionDescription,
        }),
      };
    });

    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(object);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer: ArrayBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      this.saveAsExcelFile(excelBuffer, "Production Workflow");
    });
  }

  saveAsExcelFile(buffer: ArrayBuffer, fileName: string): void {
    const EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(
      data,
      fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
    );
  }
}
